import { audio } from '../../../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../../../state/SaveManager.js';
import { applicationLifecycle } from '../../../../../shared/lifecycle/index.js';
import { NUMBER_KINGDOM_ASSETS } from '../../../NumberKingdomAssets.js';
import { NUMBER_KINGDOM_DISTRICTS, getNumberKingdomState } from '../../../NumberKingdomState.js';
import {
  configureNumberKingdomDistrictRenderer,
  getNumberKingdomDistrictLayout,
} from '../../../components/NumberKingdomDistrictLayout.js';
import {
  createNumberKingdomHomeButton,
  type NumberKingdomHomeButton,
} from '../../../components/NumberKingdomHomeButton.js';
import {
  completeNumberForge,
  discoverNumberForge,
  getAdditionCitadelState,
} from '../AdditionCitadelState.js';
import { ADDITION_CITADEL_NUMBER_FORGE_1 } from '../minigames/NumberForgeConfig.js';
import type { NumberForgeSnapshot } from '../minigames/NumberForgeState.js';
import {
  NUMBER_FORGE_ASSETS,
  NUMBER_FORGE_ASSET_LIST,
  canEnterNumberForge,
  getMissingNumberForgeImages,
} from './NumberForgeAssets.js';
import { NumberForgeView } from './NumberForgeView.js';
import { canEnterRegroupingWorkshop } from '../regrouping-workshop/RegroupingWorkshopAssets.js';
import { REGROUPING_WORKSHOP_SCENE_KEY } from '../regrouping-workshop/RegroupingWorkshopScene.js';

export const NUMBER_FORGE_SCENE_KEY = 'NumberForge';

const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';

export class NumberForgeScene extends Phaser.Scene {
  static readonly requiredImageKeys = NUMBER_FORGE_ASSET_LIST
    .filter(asset => asset.required)
    .map(asset => asset.key);

  private background!: Phaser.GameObjects.Image;
  private poweredEnvironment!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private backLabel!: Phaser.GameObjects.Text;
  private homeButton!: NumberKingdomHomeButton;
  private successNavigation?: Phaser.GameObjects.Text;
  private view?: NumberForgeView;
  private reducedMotion = false;
  private completed = false;
  private completedOnEntry = false;
  private transitioning = false;
  private unsubscribeLifecycle?: () => void;
  private resizeTimer?: number;

  constructor() {
    super({ key: NUMBER_FORGE_SCENE_KEY });
  }

  getProductionReadiness() {
    const missingAssets = getMissingNumberForgeImages(this.textures);
    return { ready: canEnterNumberForge(this.textures), missingAssets } as const;
  }

  create() {
    const world = getNumberKingdomState();
    const citadel = getAdditionCitadelState();
    if (!world.bridgeCrossingUnlocked
      || !world.districtUnlocks.includes(NUMBER_KINGDOM_DISTRICTS.additionCitadel)
      || !citadel.completed) {
      this.scene.start('AdditionCitadel');
      return;
    }

    const readiness = this.getProductionReadiness();
    if (!readiness.ready) {
      console.error('[NumberForge] Required production textures are missing:',
        readiness.missingAssets.map(asset => `${asset.key} (${asset.path})`));
      this.scene.start('AdditionCitadel');
      return;
    }

    configureNumberKingdomDistrictRenderer(this);
    void audio.enterSoundscape('number-kingdom');
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.completedOnEntry = citadel.numberForgeChallengesCompleted
      .includes(ADDITION_CITADEL_NUMBER_FORGE_1.id);
    this.completed = false;
    this.transitioning = false;
    discoverNumberForge();
    SaveManager.setLastScene(NUMBER_FORGE_SCENE_KEY);
    SaveManager.save();

    this.background = this.add.image(640, 360, NUMBER_FORGE_ASSETS.workshopBackground.key)
      .setCrop(22, 0, 840, 472).setDisplaySize(1280, 720).setDepth(0);
    this.poweredEnvironment = this.add.image(640, 360, NUMBER_FORGE_ASSETS.workshopForeground.key)
      .setCrop(20, 0, 840, 472).setDisplaySize(1280, 720)
      .setAlpha(this.completedOnEntry ? 1 : 0).setDepth(2);
    this.cosmo = this.add.image(140, 610, NUMBER_KINGDOM_ASSETS.numberKingdomCosmo.key)
      .setDisplaySize(145, 129).setDepth(16);
    this.backLabel = this.add.text(20, 20, '←  Addition Citadel', {
      fontFamily: FONT_UI,
      fontSize: '18px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#2a1524',
      strokeThickness: 6,
    }).setDepth(110).setInteractive({ useHandCursor: true });
    this.homeButton = createNumberKingdomHomeButton(this, this.returnHome);

    this.backLabel.on('pointerdown', this.returnToCitadel, this);
    this.input.keyboard?.on('keydown-ESC', this.returnToCitadel, this);
    this.input.keyboard?.on('keydown-M', this.returnHome, this);
    this.input.keyboard?.on('keydown-C', this.returnToCitadelAfterSuccess, this);
    this.input.keyboard?.on('keydown-R', this.enterRegroupingWorkshop, this);
    window.addEventListener('resize', this.handleResize);
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => this.view?.setSuspended(true),
      onResume: () => this.view?.setSuspended(false),
    });
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);

    this.view = new NumberForgeView(this, ADDITION_CITADEL_NUMBER_FORGE_1, {
      reducedMotion: this.reducedMotion,
      celebrateCompletion: !this.completedOnEntry,
      onComplete: this.handleComplete,
    });
    this.layout();
    if (this.completedOnEntry) this.restorePoweredChallenge();
  }

  private restorePoweredChallenge() {
    const controller = this.view?.controller;
    if (!controller) return;
    controller.dispatch({ type: 'reveal-source', source: 'a' });
    controller.dispatch({ type: 'reveal-source', source: 'b' });
    controller.dispatch({ type: 'move', source: 'a', place: 'tens', amount: 3 });
    controller.dispatch({ type: 'move', source: 'b', place: 'tens', amount: 2 });
    controller.dispatch({ type: 'move', source: 'a', place: 'ones', amount: 4 });
    controller.dispatch({ type: 'move', source: 'b', place: 'ones', amount: 5 });
    controller.dispatch({ type: 'charge' });
  }

  private readonly handleComplete = (_snapshot: NumberForgeSnapshot) => {
    if (this.completed) return;
    this.completed = true;
    const result = completeNumberForge(ADDITION_CITADEL_NUMBER_FORGE_1);
    SaveManager.setLastScene(NUMBER_FORGE_SCENE_KEY);
    SaveManager.save();
    this.showPoweredWorldReaction(result.firstCompletion && !this.completedOnEntry);
  };

  private showPoweredWorldReaction(celebrate: boolean) {
    this.successNavigation?.destroy();
    this.successNavigation = this.add.text(
      640,
      620,
      'R / tap: Regrouping Workshop  •  C: Citadel  •  M: Home Hub',
      {
        fontFamily: FONT_UI,
        fontSize: '17px',
        fontStyle: 'bold',
        color: '#fff2ba',
        stroke: '#2a1524',
        strokeThickness: 6,
      },
    ).setOrigin(0.5).setDepth(112).setInteractive({ useHandCursor: true });
    this.successNavigation.on('pointerup', this.enterRegroupingWorkshop, this);

    if (!celebrate || this.reducedMotion) {
      this.poweredEnvironment.setAlpha(1);
      // Completion can be emitted while NumberForgeView is still refreshing
      // its text textures. Relayout on the next scene tick to avoid updating a
      // texture that Phaser is replacing in that same refresh.
      this.time.delayedCall(0, () => {
        if (this.scene.isActive()) this.layout();
      });
      return;
    }

    audio.restoration();
    this.tweens.add({
      targets: this.poweredEnvironment,
      alpha: 1,
      duration: 700,
      ease: 'Sine.easeOut',
    });
    const burst = this.add.image(640, 355, NUMBER_FORGE_ASSETS.powerBurst.key)
      .setCrop(16, 16, 322, 238).setDisplaySize(300, 220)
      .setBlendMode(Phaser.BlendModes.ADD).setAlpha(0).setScale(0.72).setDepth(108);
    this.tweens.add({
      targets: burst,
      alpha: { from: 0, to: 0.92 },
      scaleX: 1.25,
      scaleY: 1.25,
      duration: 560,
      yoyo: true,
      hold: 120,
      ease: 'Sine.easeOut',
      onComplete: () => burst.destroy(),
    });
    this.time.delayedCall(0, () => {
      if (this.scene.isActive()) this.layout();
    });
  }

  private readonly returnToCitadelAfterSuccess = () => {
    if (this.completed) this.returnToCitadel();
  };

  private readonly enterRegroupingWorkshop = () => {
    if (this.transitioning || !this.completed || !canEnterRegroupingWorkshop(this.textures)) return;
    this.transitioning = true;
    SaveManager.save();
    this.scene.start(REGROUPING_WORKSHOP_SCENE_KEY);
  };

  private readonly returnToCitadel = () => {
    if (this.transitioning) return;
    this.transitioning = true;
    SaveManager.save();
    this.scene.start('AdditionCitadel');
  };

  private readonly returnHome = () => {
    if (this.transitioning) return;
    this.transitioning = true;
    SaveManager.setLastScene(NUMBER_FORGE_SCENE_KEY);
    SaveManager.save();
    this.scene.start('HomeHub');
  };

  private readonly handleResize = () => {
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      configureNumberKingdomDistrictRenderer(this);
      this.layout();
    }, 80);
  };

  private layout() {
    const { safe, compact } = getNumberKingdomDistrictLayout(this);
    this.backLabel?.setPosition(safe.x + (compact ? 182 : 206), safe.y + 12)
      .setFontSize(compact ? 15 : 18);
    this.homeButton.layout(safe, compact, 'left');
    this.cosmo?.setPosition(safe.x + (compact ? 68 : 82), safe.bottom - (compact ? 64 : 73))
      .setDisplaySize(compact ? 112 : 132, compact ? 100 : 117);
    this.successNavigation?.setPosition(safe.centerX, safe.bottom - (compact ? 100 : 112))
      .setFontSize(compact ? 14 : 17);
    this.view?.layout(safe, compact);
  }

  private shutdown() {
    SaveManager.save();
    this.unsubscribeLifecycle?.();
    this.unsubscribeLifecycle = undefined;
    audio.leaveSoundscape({ id: 'number-kingdom' });
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.input.keyboard?.off('keydown-ESC', this.returnToCitadel, this);
    this.input.keyboard?.off('keydown-M', this.returnHome, this);
    this.input.keyboard?.off('keydown-C', this.returnToCitadelAfterSuccess, this);
    this.input.keyboard?.off('keydown-R', this.enterRegroupingWorkshop, this);
    this.successNavigation?.off('pointerup', this.enterRegroupingWorkshop, this);
    this.view?.destroy();
    this.view = undefined;
    this.tweens.killAll();
  }
}
